import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './fonts';
import './styles.css';
import { SessionProvider, useSession } from './lib/session';
import { ToastProvider } from './lib/ui';
import Shell from './components/Shell';
import { THEMES } from './lib/themes';

const Login = lazy(() => import('./pages/Login'));
const Lobby = lazy(() => import('./pages/Lobby'));
const Play = lazy(() => import('./pages/Play'));
const Embed = lazy(() => import('./pages/Embed'));
const Replay = lazy(() => import('./pages/Replay'));
const History = lazy(() => import('./pages/History'));
// lobby themes from the nav: Sports (by sport), Classics, Asian, Fantasy & adventure, Sci-fi & heroes
const ThemePage = lazy(() => import('./pages/ThemePage'));
const Admin = lazy(() => import('./admin/Admin'));
const SkinList = lazy(() => import('./editor/SkinList'));
const SkinEditor = lazy(() => import('./editor/SkinEditor'));
const Harness = lazy(() => import('./pages/Harness'));
const Provider = lazy(() => import('./provider/Provider'));
// table games (tg01-tg10)
const TablesLobby = lazy(() => import('./tables/TablesLobby'));
const TablePage = lazy(() => import('./tables/TablePage'));
const TableEmbed = lazy(() => import('./tables/TablePage').then((m) => ({ default: m.TableEmbed })));
const TableReplay = lazy(() => import('./tables/TableReplay'));
const InviteAccept = lazy(() => import('./tables/InviteAccept'));

function RequireAuth({ children }: { children: JSX.Element }) {
  const { me, loading } = useSession();
  if (loading) return <div className="page muted" style={{ padding: 24 }}>Loading…</div>;
  if (!me) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <Suspense fallback={<div style={{ padding: 24 }} className="muted">Loading…</div>}>
      <Routes>
        <Route path="/embed/table/:tableId" element={<TableEmbed />} />
        <Route path="/embed/:gameId" element={<Embed />} />
        <Route path="/invite" element={<InviteAccept />} />
        {import.meta.env.DEV && <Route path="/dev/harness" element={<Harness />} />}
        <Route element={<Shell />}>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<RequireAuth><Lobby /></RequireAuth>} />
          <Route path="/play/:gameId" element={<RequireAuth><Play /></RequireAuth>} />
          <Route path="/tables" element={<RequireAuth><TablesLobby /></RequireAuth>} />
          <Route path="/table/:tableId" element={<RequireAuth><TablePage /></RequireAuth>} />
          <Route path="/table-replay/:roundId" element={<RequireAuth><TableReplay /></RequireAuth>} />
          <Route path="/replay/:roundId" element={<RequireAuth><Replay /></RequireAuth>} />
          <Route path="/history" element={<RequireAuth><History /></RequireAuth>} />
          {THEMES.map((t) => (
            <Route key={t.id} path={t.path} element={<RequireAuth><ThemePage key={t.id} themeId={t.id} /></RequireAuth>} />
          ))}
          <Route path="/admin/*" element={<RequireAuth><Admin /></RequireAuth>} />
          <Route path="/provider/*" element={<RequireAuth><Provider /></RequireAuth>} />
          <Route path="/editor" element={<RequireAuth><SkinList /></RequireAuth>} />
          <Route path="/editor/:skinId/:versionRef" element={<RequireAuth><SkinEditor /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <SessionProvider>
          <App />
        </SessionProvider>
      </ToastProvider>
    </BrowserRouter>
  </StrictMode>,
);
